/** Редактор кнопок, меню выбора и рядов компонентов (общий для классики и V2). */
import { cloneWithNewIds, newButton, newSelect, newSelectOption } from '../../../src/shared/factory.js';
import { LIMITS } from '../../../src/shared/limits.js';
import type { ButtonStyleName, DAction, DActionRow, DButton, DSelect } from '../../../src/shared/types.js';
import { h, icon } from '../dom.js';
import { app } from '../ctx.js';
import { store } from '../store.js';
import { emojiField, numberField, selectField, switchField, textField } from '../ui/inputs.js';
import { addButton, emptyHint, group, itemCard, listActions, row, segmented } from './common.js';

const STYLE_LABELS: { value: ButtonStyleName; label: string; cls: string }[] = [
  { value: 'primary', label: 'Синяя', cls: 'seg-primary' },
  { value: 'secondary', label: 'Серая', cls: 'seg-secondary' },
  { value: 'success', label: 'Зелёная', cls: 'seg-success' },
  { value: 'danger', label: 'Красная', cls: 'seg-danger' },
  { value: 'link', label: 'Ссылка', cls: 'seg-link' },
];

/* ---------------- действие ---------------- */

/** Кнопки, которые обрабатывают другие боты сервера. */
const KNOWN_BOT_BUTTONS: { id: string; label: string; hint: string }[] = [
  {
    id: 'appeal:create',
    label: 'Подать апелляцию (бот Moderation)',
    hint: 'Сохраните сообщение как шаблон, скопируйте ссылку на шаблон и опубликуйте его командой !appeal <ссылка>.',
  },
  {
    id: 'event_order:open',
    label: 'Заказать игру (бот Events)',
    hint: 'По кнопке участник увидит список игр и отправит заявку ведущим. Сохраните сообщение как шаблон и опубликуйте командой !games <ссылка>. С этой кнопкой бот не добавляет меню игр под сообщением.',
  },
  {
    id: 'v3_create_btn',
    label: 'Создать личную комнату (бот Main)',
    hint: 'По кнопке участник заполнит форму (название и цвет роли), заявка уйдёт на модерацию. Сохраните сообщение как шаблон и опубликуйте командой !privroom_setup <ссылка>. Если кнопки нет в шаблоне, бот добавит её сам; подпись и цвет вашей кнопки он не меняет.',
  },
];

export function actionEditor(getAction: () => DAction, setAction: (a: DAction) => void, key: string): HTMLElement {
  const a = getAction();
  const wrap = h('div', { class: 'action-editor' });
  const g = app.guild;
  const types: { value: DAction['type']; label: string }[] = [
    { value: 'none', label: 'Ничего не делать' },
    { value: 'template', label: 'Ответить шаблоном (видит только нажавший)' },
    { value: 'role', label: 'Выдать / снять роль' },
  ];
  types.push({ value: 'raw', label: 'Кнопка другого бота (свой ID)' });

  const detail = h('div', { class: 'action-detail' });
  const renderDetail = () => {
    const cur = getAction();
    detail.replaceChildren();
    if (cur.type === 'template') {
      const tpls = g?.templates ?? [];
      if (!tpls.length) {
        detail.append(h('div', { class: 'field-hint warn' }, icon('info', 14), 'На сервере ещё нет шаблонов - сохраните какой-нибудь в «Шаблоны».'));
      }
      detail.append(
        selectField(
          'Шаблон',
          cur.templateId,
          [
            { value: '', label: '- выберите шаблон -' },
            ...(cur.templateId && !tpls.some((t) => t.id === cur.templateId) ? [{ value: cur.templateId, label: 'Шаблон из папки, недоступной вам' }] : []),
            ...tpls.map((t) => ({ value: t.id, label: t.name })),
          ],
          (v) => store.input(`${key}.tpl`, () => setAction({ type: 'template', templateId: v })),
        ),
      );
    } else if (cur.type === 'role') {
      const roles = (g?.roles ?? []).filter((r) => !r.everyone && !r.managed);
      const options = roles.map((r) => {
        const tooHighForBot = g ? r.position >= g.botTopRole : false;
        const tooHighForUser = g ? r.position >= g.userTopRole : false;
        return { value: r.id, label: `${r.name}${tooHighForBot ? ' (выше роли бота)' : tooHighForUser ? ' (выше вашей роли)' : ''}` };
      });
      detail.append(
        row(
          selectField('Роль', cur.roleId, [{ value: '', label: '- выберите роль -' }, ...options], (v) =>
            store.input(`${key}.role`, () => setAction({ ...(getAction() as Extract<DAction, { type: 'role' }>), roleId: v })),
          ),
          selectField(
            'Режим',
            cur.mode,
            [
              { value: 'toggle', label: 'Переключать' },
              { value: 'add', label: 'Только выдать' },
              { value: 'remove', label: 'Только снять' },
            ],
            (v) => store.input(`${key}.mode`, () => setAction({ ...(getAction() as Extract<DAction, { type: 'role' }>), mode: v })),
          ),
        ),
      );
      if (g && !g.canManageRoles) {
        detail.append(h('div', { class: 'field-hint warn' }, icon('alert', 14), 'Чтобы отправить кнопку выдачи роли, нужно право «Управлять ролями».'));
      }
    } else if (cur.type === 'raw') {
      const preset = KNOWN_BOT_BUTTONS.find((k) => k.id === cur.customId)?.id ?? '';
      detail.append(
        selectField(
          'Готовые кнопки ботов сервера',
          preset,
          [{ value: '', label: '- свой ID -' }, ...KNOWN_BOT_BUTTONS.map((k) => ({ value: k.id, label: k.label }))],
          (v) => {
            if (!v) return;
            store.input(`${key}.raw`, () => setAction({ type: 'raw', customId: v }));
            renderDetail();
          },
        ),
        textField({
          label: 'custom_id',
          value: cur.customId,
          max: LIMITS.customId,
          mono: true,
          onInput: (v) => store.input(`${key}.raw`, () => setAction({ type: 'raw', customId: v.trim() })),
          hint: 'Нажатие обработает бот, который опубликует это сообщение. Сам Embed Studio на такую кнопку не отвечает.',
        }),
      );
      const known = KNOWN_BOT_BUTTONS.find((k) => k.id === cur.customId);
      if (known) detail.append(h('div', { class: 'field-hint' }, icon('info', 14), known.hint));
    }
  };
  wrap.append(
    selectField('При нажатии', a.type, types, (v) => {
      store.input(`${key}.type`, () => {
        if (v === 'none') setAction({ type: 'none' });
        else if (v === 'template') setAction({ type: 'template', templateId: '' });
        else if (v === 'role') setAction({ type: 'role', roleId: '', mode: 'toggle' });
        else setAction({ type: 'raw', customId: '' });
      });
      renderDetail();
    }),
    detail,
  );
  renderDetail();
  return wrap;
}

/* ---------------- кнопка ---------------- */

export function buttonFields(b: DButton, opts: { allowLink?: boolean } = {}): HTMLElement[] {
  const linkBox = h('div');
  const actionBox = h('div');
  const renderKind = () => {
    linkBox.replaceChildren();
    actionBox.replaceChildren();
    if (b.style === 'link') {
      linkBox.append(
        textField({
          label: 'Ссылка',
          value: b.url,
          max: LIMITS.linkUrl,
          placeholder: 'https://',
          required: true,
          onInput: (v) => store.input(`${b.id}.url`, () => (b.url = v)),
        }),
      );
    } else {
      actionBox.append(actionEditor(() => b.action, (a) => (b.action = a), b.id));
    }
  };
  renderKind();
  const styles = opts.allowLink === false ? STYLE_LABELS.filter((s) => s.value !== 'link') : STYLE_LABELS;
  return [
    h(
      'div',
      { class: 'field' },
      h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Стиль')),
      segmented(styles, b.style, (v) => {
        store.input(`${b.id}.style`, () => {
          b.style = v;
          if (v === 'link' && !b.url) b.url = 'https://discord.com';
        });
        renderKind();
      }, 'Стиль кнопки'),
    ),
    row(
      textField({ label: 'Текст', value: b.label, max: LIMITS.buttonLabel, placeholder: 'Текст кнопки', onInput: (v) => store.input(`${b.id}.label`, () => (b.label = v)) }),
      emojiField('Эмодзи', b.emoji, (v) => store.input(`${b.id}.emoji`, () => (b.emoji = v))),
    ),
    linkBox,
    actionBox,
    switchField('Неактивна', b.disabled, (v) => store.input(`${b.id}.disabled`, () => (b.disabled = v))),
  ];
}

/* ---------------- меню выбора ---------------- */

function selectFields(s: DSelect): HTMLElement[] {
  const opts = h(
    'div',
    { class: 'nested-list' },
    s.options.map((o, i) =>
      itemCard({
        id: o.id,
        level: 2,
        icon: 'chevronRight',
        title: o.label || `Вариант ${i + 1}`,
        subtitle: o.description || undefined,
        defaultCollapsed: s.options.length > 3,
        actions: listActions(s.options, i, (x) => cloneWithNewIds(x)),
        body: () => [
          row(
            textField({ label: 'Название', value: o.label, max: LIMITS.optionLabel, required: true, onInput: (v) => store.input(`${o.id}.label`, () => (o.label = v)) }),
            emojiField('Эмодзи', o.emoji, (v) => store.input(`${o.id}.emoji`, () => (o.emoji = v))),
          ),
          textField({ label: 'Описание', value: o.description, max: LIMITS.optionDescription, onInput: (v) => store.input(`${o.id}.desc`, () => (o.description = v)) }),
          s.rawCustomId
            ? textField({ label: 'value', value: o.rawValue ?? '', mono: true, max: LIMITS.optionValue, onInput: (v) => store.input(`${o.id}.raw`, () => (o.rawValue = v)) })
            : actionEditor(() => o.action, (a) => (o.action = a), o.id),
          switchField('Выбран по умолчанию', o.isDefault, (v) => store.input(`${o.id}.def`, () => (o.isDefault = v))),
        ],
      }),
    ),
  );
  return [
    textField({ label: 'Подсказка', value: s.placeholder, max: LIMITS.selectPlaceholder, placeholder: 'Выберите вариант', onInput: (v) => store.input(`${s.id}.ph`, () => (s.placeholder = v)) }),
    row(
      numberField('Мин. выбор', s.minValues, 0, 25, (v) => store.input(`${s.id}.min`, () => (s.minValues = v))),
      numberField('Макс. выбор', s.maxValues, 1, 25, (v) => store.input(`${s.id}.max`, () => (s.maxValues = v))),
    ),
    switchField('Неактивно', s.disabled, (v) => store.input(`${s.id}.dis`, () => (s.disabled = v))),
    group(`Варианты (${s.options.length}/${LIMITS.selectOptions})`, opts, addButton('Вариант', () => store.change(() => s.options.push(newSelectOption(s.options.length + 1))), { disabled: s.options.length >= LIMITS.selectOptions, small: true })),
  ];
}

/* ---------------- ряд ---------------- */

export function rowEditor(r: DActionRow, title: string, actions: ReturnType<typeof listActions>, level: 0 | 1 = 0): HTMLElement {
  const hasSelect = r.items.some((i) => i.type === 'select');
  const canAddButton = !hasSelect && r.items.length < LIMITS.buttonsPerRow;
  const canAddSelect = r.items.length === 0;
  return itemCard({
    id: r.id,
    level,
    icon: 'mouse',
    title,
    subtitle: r.items.length ? r.items.map((i) => (i.type === 'button' ? i.label || i.emoji || 'кнопка' : 'меню выбора')).join(' · ') : 'пусто',
    actions,
    body: () => [
      r.items.length
        ? h(
            'div',
            { class: 'nested-list' },
            r.items.map((it, i) =>
              it.type === 'button'
                ? itemCard({
                    id: it.id,
                    level: (level + 1) as 1 | 2,
                    icon: it.style === 'link' ? 'link' : 'mouse',
                    accent: `var(--btn-${it.style})`,
                    title: it.label || it.emoji || `Кнопка ${i + 1}`,
                    subtitle: it.style === 'link' ? it.url : actionSummary(it.action),
                    defaultCollapsed: r.items.length > 2,
                    actions: listActions(r.items, i, (x) => cloneWithNewIds(x)),
                    body: () => buttonFields(it),
                  })
                : itemCard({
                    id: it.id,
                    level: (level + 1) as 1 | 2,
                    icon: 'menu',
                    title: it.placeholder || 'Меню выбора',
                    subtitle: `${it.options.length} вар.`,
                    actions: listActions(r.items, i, (x) => cloneWithNewIds(x)),
                    body: () => selectFields(it),
                  }),
            ),
          )
        : emptyHint('Добавьте кнопки (до 5) или одно меню выбора'),
      h(
        'div',
        { class: 'btn-row' },
        addButton('Кнопка', () => store.change(() => r.items.push(newButton(r.items.length ? 'secondary' : 'primary'))), { disabled: !canAddButton, small: true }),
        addButton('Меню выбора', () => store.change(() => r.items.push(newSelect())), { disabled: !canAddSelect, small: true }),
      ),
    ],
  });
}

export function actionSummary(a: DAction): string {
  switch (a.type) {
    case 'template': {
      const t = app.guild?.templates.find((x) => x.id === a.templateId);
      return t ? `шаблон «${t.name}»` : a.templateId ? 'шаблон из закрытой папки' : 'шаблон не выбран';
    }
    case 'role': {
      const r = app.guild?.roles.find((x) => x.id === a.roleId);
      const verb = a.mode === 'add' ? 'выдать' : a.mode === 'remove' ? 'снять' : 'роль';
      return r ? `${verb} @${r.name}` : 'роль не выбрана';
    }
    case 'raw':
      return `custom_id ${a.customId}`;
    default:
      return 'без действия';
  }
}
